import {
  AUDIT_LABELS,
  BOOKING_ISSUE_OPTIONS,
  BOOKING_STATUS_TONE,
  LOG_CATEGORY_DEFS,
} from "./adminConstants";
import { NUMBER_LOCALE, formatDecimal } from "../../utils/numberFormat";

const CURRENCY_FORMATTERS = new Map();
const COMPACT_CURRENCY_FORMATTERS = new Map();

export const TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
});

export const DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
});

export const MONTH_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  month: "long",
});

const getCurrencyFormatter = (currency) => {
  const normalized = (currency || "EUR").toUpperCase();
  if (!CURRENCY_FORMATTERS.has(normalized)) {
    CURRENCY_FORMATTERS.set(
      normalized,
      new Intl.NumberFormat(NUMBER_LOCALE, {
        style: "currency",
        currency: normalized,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }
  return CURRENCY_FORMATTERS.get(normalized);
};

const getCompactCurrencyFormatter = (currency) => {
  const normalized = (currency || "EUR").toUpperCase();
  if (!COMPACT_CURRENCY_FORMATTERS.has(normalized)) {
    COMPACT_CURRENCY_FORMATTERS.set(
      normalized,
      new Intl.NumberFormat(NUMBER_LOCALE, {
        style: "currency",
        currency: normalized,
        notation: "compact",
        maximumFractionDigits: 1,
      })
    );
  }
  return COMPACT_CURRENCY_FORMATTERS.get(normalized);
};

export const formatCurrency = (value, currency) =>
  getCurrencyFormatter(currency).format(Number.isFinite(value) ? value : 0);

export const formatCurrencyCompact = (value, currency) =>
  getCompactCurrencyFormatter(currency).format(Number.isFinite(value) ? value : 0);

export const formatAmountInput = (value) =>
  Number.isFinite(value) ? formatDecimal(value, 2) : "";

export const parseAmount = (value) => {
  if (typeof value !== "string") {
    return Number.isFinite(value) ? value : 0;
  }
  const trimmed = value.trim().replace(/\s/g, "");
  const normalized = trimmed.includes(",")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed.replace(/,/g, "");
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatBookingSlot = (startUtc, endUtc) => {
  const start = new Date(startUtc);
  const end = new Date(endUtc);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "—";
  }
  return `${DATE_FORMATTER.format(start)} · ${TIME_FORMATTER.format(start)}–${TIME_FORMATTER.format(end)}`;
};

export const formatDurationMinutes = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";
  const totalMinutes = Math.round(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

export const generatePinCode = () => {
  const length = Math.floor(Math.random() * 3) + 4;
  let pin = "";
  for (let i = 0; i < length; i += 1) {
    pin += Math.floor(Math.random() * 10).toString();
  }
  return pin;
};

export const generatePromoCode = ({ discount, discountAmount, usageLimit, validity } = {}) => {
  const parts = ["WAYLI"];
  const discountMatch = `${discount ?? ""}`.match(/(\d+(?:\.\d+)?)/);
  const amountMatch = `${discountAmount ?? ""}`.match(/(\d+(?:\.\d+)?)/);
  const numericMatch = discountMatch || amountMatch;
  if (numericMatch) {
    const discountValue = Number.parseFloat(numericMatch[1]);
    if (Number.isFinite(discountValue) && discountValue > 0) {
      parts.push(`${Math.round(discountValue)}OFF`);
    }
  }
  const usageValue = Number.parseInt(usageLimit, 10);
  if (Number.isFinite(usageValue) && usageValue > 0) {
    parts.push(`U${usageValue}`);
  }
  const validityMatch = `${validity ?? ""}`.trim().match(/^(\d+)\s*(d|day|days)?$/i);
  if (validityMatch) {
    parts.push(`D${validityMatch[1]}`);
  }
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let suffix = "";
  for (let i = 0; i < 4; i += 1) {
    suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  parts.push(suffix);
  return parts.join("-");
};

export const buildEquipmentLabel = (items, fallback = "Equipment") => {
  if (!Array.isArray(items) || items.length === 0) {
    return fallback;
  }
  return items
    .map((item) => `${item.quantity}× ${item.equipmentTypeName || fallback}`)
    .join(", ");
};

const isLateCheckIn = (checkedInUtc, endUtc) => {
  if (!checkedInUtc || !endUtc) return false;
  const checkedInTime = new Date(checkedInUtc).getTime();
  const endTime = new Date(endUtc).getTime();
  if (Number.isNaN(checkedInTime) || Number.isNaN(endTime)) return false;
  return checkedInTime > endTime;
};

export const mapAdminBookingSummary = (booking) => {
  const items = Array.isArray(booking.items) ? booking.items : [];
  const totalPaid = booking.paidAmount ?? booking.totalAmount ?? 0;
  const refundedAmount = booking.refundedAmount ?? 0;
  const currency = booking.currency || "EUR";
  const guestLabel = booking.contactName || booking.contactEmail || "Guest";
  const lateReturn = isLateCheckIn(booking.checkedInUtc, booking.endUtc);
  const bookingToken = booking.bookingToken || null;
  return {
    id: booking.id,
    contactName: booking.contactName,
    contactEmail: booking.contactEmail,
    contactPhone: booking.contactPhone,
    guest: guestLabel,
    item: booking.type === "Event"
      ? booking.eventTitle || "Event"
      : buildEquipmentLabel(items),
    pickup: booking.locationName || "Unknown location",
    locationId: booking.locationId,
    eventId: booking.eventId,
    eventTitle: booking.eventTitle,
    time: formatBookingSlot(booking.startUtc, booking.endUtc),
    status: booking.status,
    statusTone: BOOKING_STATUS_TONE[booking.status] || "neutral",
    totalPaid,
    refundedAmount,
    currency,
    paymentStatus: booking.paymentStatus,
    createdVia: booking.createdVia,
    adminNote: booking.adminNote,
    items,
    type: booking.type,
    rewardVoucherCode: booking.rewardVoucherCode,
    checkedOutUtc: booking.checkedOutUtc,
    checkedInUtc: booking.checkedInUtc,
    lateReturn,
    issueTag: booking.issueTag,
    startUtc: booking.startUtc,
    endUtc: booking.endUtc,
    bookingToken,
  };
};

export const mapAdminBookingDetail = (booking) => {
  const summary = mapAdminBookingSummary(booking);
  const refunds = booking.payment?.refunds ?? [];
  const refundItems = refunds.map((refund) => ({
    id: refund.id,
    time: TIME_FORMATTER.format(new Date(refund.createdUtc)),
    amount: refund.amount,
    status: refund.status,
  }));
  const bookingToken = booking.bookingToken || null;
  return {
    ...summary,
    bookingToken,
    payment: booking.payment,
    refunds: refundItems,
    cancellationReason: booking.cancellationReason,
    cancelledUtc: booking.cancelledUtc,
    paymentIntentId: booking.paymentIntentId,
  };
};

export const mapAdminLocation = (location) => ({
  id: location.id,
  name: location.name,
  address: location.address,
  city: location.city,
  postalCode: location.postalCode,
  country: location.country,
  timezoneId: location.timezoneId,
  operatingHours: location.operatingHours || "",
  blockedDates: Array.isArray(location.blockedDates) ? location.blockedDates : [],
  bookingLeadTimeHours: Number.isFinite(location.bookingLeadTimeHours)
    ? location.bookingLeadTimeHours
    : 0,
  latitude: location.latitude ?? null,
  longitude: location.longitude ?? null,
  supportsDayBookings: location.supportsDayBookings ?? true,
  supportsNightBookings: location.supportsNightBookings ?? true,
  isActive: location.isActive,
  inventory: Array.isArray(location.inventory)
    ? location.inventory.map((item) => ({
        ...item,
        maintenanceQuantity: item.maintenanceQuantity ?? 0,
        lostQuantity: item.lostQuantity ?? 0,
        manualInUseQuantity: item.manualInUseQuantity ?? 0,
        blocks: Array.isArray(item.blocks) ? item.blocks : [],
      }))
    : [],
});

export const formatLocationAddress = (location) => location.address || "";

export const mapAdminPartner = (partner) => ({
  id: partner.id,
  name: partner.name,
  locationId: partner.locationId,
  locationName: partner.locationName,
  isActive: partner.isActive,
  createdUtc: partner.createdUtc,
  updatedUtc: partner.updatedUtc,
  contactName: partner.contactName || "",
  contactEmail: partner.contactEmail || "",
  contactPhone: partner.contactPhone || "",
  commissionRate: Number.isFinite(partner.commissionRate)
    ? partner.commissionRate
    : 0.15,
});

export const mapAdminPartnerDetail = (partner) => ({
  id: partner.id,
  name: partner.name,
  locationId: partner.locationId,
  locationName: partner.locationName,
  isActive: partner.isActive,
  createdUtc: partner.createdUtc,
  updatedUtc: partner.updatedUtc,
  contactName: partner.contactName || "",
  contactEmail: partner.contactEmail || "",
  contactPhone: partner.contactPhone || "",
  commissionRate: Number.isFinite(partner.commissionRate)
    ? partner.commissionRate
    : 0.15,
  bookingsCount: partner.bookingsCount ?? 0,
  revenueTotal: partner.revenueTotal ?? 0,
  commissionOwed: partner.commissionOwed ?? 0,
  currency: partner.currency || "EUR",
});

export const mapAdminAuditEntry = (entry) => {
  const severity = entry.severity || "info";
  return {
    id: entry.id,
    time: formatTimestamp(entry.createdUtc),
    createdUtc: entry.createdUtc,
    actor: entry.actor || "System",
    action: entry.eventType,
    target: entry.target || entry.bookingId || "—",
    detail: entry.detail || "",
    detailJson: entry.detailJson || "",
    severity,
    label: AUDIT_LABELS[severity] || AUDIT_LABELS.info,
  };
};

export const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${DATE_FORMATTER.format(date)} · ${TIME_FORMATTER.format(date)}`;
};

export const getIssueLabel = (issueTag) => {
  if (!issueTag || issueTag === "none") return "—";
  const match = BOOKING_ISSUE_OPTIONS.find((option) => option.value === issueTag);
  return match ? match.label : issueTag;
};

export const formatRelativeTime = (value) => {
  if (!value) return "—";
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return "—";
  const diffMs = Date.now() - timestamp;
  if (diffMs < 60_000) return "Just now";
  if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)} min ago`;
  if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`;
  return DATE_FORMATTER.format(new Date(timestamp));
};

export const formatPercent = (value) => {
  if (!Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
};

export const formatPercentInput = (rate) => {
  if (!Number.isFinite(rate)) return "";
  const percent = rate * 100;
  return Number.isInteger(percent) ? String(percent) : formatDecimal(percent, 1);
};

export const formatCommissionRateInput = (rate) => formatPercentInput(rate);

export const parseCountInput = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const formatIsoDate = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export const parseDateInput = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
};

export const formatShortId = (value) => {
  if (!value) return "—";
  return String(value).replace(/-/g, "").slice(0, 8).toUpperCase();
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (text.includes('"') || text.includes(",") || text.includes("\n")) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export const buildAuditCsv = (entries) => {
  const header = ["createdUtc", "event", "target", "actor", "detail", "severity"];
  const rows = entries.map((entry) => [
    entry.createdUtc || entry.time,
    entry.action,
    entry.target,
    entry.actor,
    entry.detail,
    entry.severity,
  ]);
  return [header, ...rows]
    .map((row) => row.map(escapeCsvValue).join(","))
    .join("\n");
};

export const normalizeLogSeverity = (value) => {
  const lowered = String(value || "info").toLowerCase();
  if (lowered === "critical") return "critical";
  if (lowered === "warning" || lowered === "ops") return "warning";
  return "info";
};

export const getLogCategory = (entry) => {
  const haystack = `${entry.action} ${entry.detail} ${entry.target}`.toLowerCase();
  const match = LOG_CATEGORY_DEFS.find((category) =>
    category.keywords.some((keyword) => haystack.includes(keyword))
  );
  return match || LOG_CATEGORY_DEFS[LOG_CATEGORY_DEFS.length - 1];
};

export const getLogActorType = (entry, partners = []) => {
  const actor = (entry.actor || "").trim();
  const actorLower = actor.toLowerCase();
  if (!actor || actorLower === "system") return "system";
  const partnerMatch = partners.some(
    (partner) => partner.name && partner.name.toLowerCase() === actorLower
  );
  if (partnerMatch) return "partner";
  if (actor.includes("@")) return "owner";
  return "staff";
};

export const buildLogSentence = (entry) => {
  if (entry.detail) return entry.detail;
  if (entry.action && entry.target) return `${entry.action} · ${entry.target}`;
  return entry.action || "Audit event";
};

export const parseLogStructuredData = (entry) => {
  const raw = (entry.detailJson || entry.detail || "").trim();
  if (!raw) return null;
  if (raw.startsWith("{") || raw.startsWith("[")) {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
};

export const getActorBadgeLabel = (entry) => {
  const actor = (entry.actor || "").trim();
  return actor ? actor : "System";
};

export const isGuid = (value) =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
